
import {useState, useLayoutEffect, useRef, useCallback, SetStateAction, useEffect} from 'react';

type SetStateWithCallback<T> = (
    value: SetStateAction<T>,
    callback?: (newState: T) => void
) => void;

/**
 * Custom React hook that extends useState with an optional callback function
 * that is called after the state has been set, regardless of whether the state changed.
 *
 * @param initialState - The initial state value or a function that returns the initial state
 * @returns A tuple containing the current state and a setState function with callback support
 */
export default function useStateWithCallback<T>(
    initialState: T | (() => T)
): [T, SetStateWithCallback<T>] {
    const [state, setState] = useState<T>(initialState);
    const callbackQueueRef = useRef<Array<(newState: T) => void>>([]);
    const stateRef = useRef<T>(state);

    // Keep stateRef in sync with state
    useLayoutEffect(() => {
        stateRef.current = state;
    }, [state]);

    // Process callbacks after each render
    useEffect(() => {
        // Process all queued callbacks
        if (callbackQueueRef.current.length > 0) {
            const callbacks = callbackQueueRef.current;
            callbackQueueRef.current = [];

            // Execute each callback with the current state
            callbacks.forEach(callback => {
                try {
                    callback(stateRef.current);
                } catch (error) {
                    // Log error but don't break the callback chain
                    console.error('Error in useStateWithCallback callback:', error);
                }
            });
        }
    });

    // Custom setState function that accepts a callback
    const setStateWithCallback = useCallback<SetStateWithCallback<T>>(
        (value: SetStateAction<T>, callback?: (newState: T) => void) => {
            // Validate callback parameter
            if (callback !== undefined && typeof callback !== 'function') {
                throw new TypeError('Callback must be a function');
            }

            // Calculate the new state value synchronously
            let newState: T;
            if (typeof value === 'function') {
                // Handle updater function
                const updater = value as (prevState: T) => T;
                newState = updater(stateRef.current);
            } else {
                // Handle direct value
                newState = value;
            }

            // Queue the callback if provided
            if (callback) {
                callbackQueueRef.current.push(() => callback(newState));
            }

            // Update the state (React may or may not actually re-render)
            setState(value);
        },
        [] // stateRef is stable, doesn't need to be in deps
    );

    return [state, setStateWithCallback];
}
